import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Avatar } from 'primereact/avatar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Skeleton } from 'primereact/skeleton';
import ProfileService from '../../services/agentProfile/ProfileService';

/**
 * Instagram-style mutual connections modal.
 * - Search at top (server-side, debounced 300ms)
 * - Simple row list — avatar + name + headline, click to navigate
 * - Auto-loads next page via IntersectionObserver on a sentinel at the list end
 *
 * Props:
 *   visible        — modal visibility
 *   onHide         — close handler
 *   profileId      — the profile whose mutuals we're listing
 *   profileName    — display name for the modal header
 *   onSelectProfile — (id) => void, called when a row is clicked (parent navigates)
 */
const PAGE_SIZE = 20;

const MutualConnectionsDialog = ({ visible, onHide, profileId, profileName, onSelectProfile }) => {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const sentinelRef = useRef(null);
    const listRef = useRef(null);

    // Debounce search input by 500ms (skill file P13 standard for InputText
    // search — 300ms is reserved for AutoComplete's native delay prop).
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 500);
        return () => clearTimeout(t);
    }, [search]);

    // Reset state whenever the modal opens or the profile / search changes.
    useEffect(() => {
        if (!visible) return;
        setItems([]);
        setPage(1);
        setLastPage(1);
        setTotal(0);
        loadPage(1, debouncedSearch, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, profileId, debouncedSearch]);

    const loadPage = useCallback(async (pageNum, q, replace = false) => {
        if (!profileId) return;
        const isFirst = replace || pageNum === 1;
        if (isFirst) setLoading(true);
        else setLoadingMore(true);
        try {
            const response = await ProfileService.getMutualConnections(profileId, {
                page: pageNum,
                per_page: PAGE_SIZE,
                q: q || undefined,
            });
            if (response.success) {
                setItems((prev) => (isFirst ? response.data : [...prev, ...response.data]));
                setPage(response.meta?.current_page ?? pageNum);
                setLastPage(response.meta?.last_page ?? 1);
                setTotal(response.meta?.total ?? 0);
            }
        } catch (err) {
            // Silent — keep whatever rows are already shown.
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [profileId]);

    // IntersectionObserver — auto-load next page when sentinel scrolls into view.
    useEffect(() => {
        if (!visible) return;
        if (page >= lastPage) return;
        if (!sentinelRef.current || !listRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && !loadingMore && !loading) {
                    loadPage(page + 1, debouncedSearch, false);
                }
            },
            { root: listRef.current, threshold: 0.1 }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [visible, page, lastPage, loading, loadingMore, debouncedSearch, loadPage]);

    const handleRowClick = (id) => {
        onHide?.();
        onSelectProfile?.(id);
    };

    const subtitleFor = (p) =>
        p.headline
        || (p.current_designation && p.current_company
            ? `${p.current_designation} · ${p.current_company}`
            : p.current_company || p.current_designation || p.location_city || '');

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={(
                <div className="flex flex-column">
                    <span className="text-900 font-semibold text-base">Mutual connections</span>
                    {profileName && (
                        <span className="text-500 text-xs font-normal mt-1">
                            with {profileName}
                            {total > 0 ? ` · ${total}` : ''}
                        </span>
                    )}
                </div>
            )}
            modal
            dismissableMask
            style={{ width: '440px' }}
            breakpoints={{ '768px': '95vw' }}
            contentStyle={{ padding: '0' }}
        >
            {/* Search bar */}
            <div className="px-3 pt-3 pb-2 surface-card border-bottom-1 surface-border">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search mutuals…"
                        className="w-full p-inputtext-sm"
                    />
                </IconField>
            </div>

            {/* List */}
            <div
                ref={listRef}
                className="overflow-y-auto"
                style={{ maxHeight: '60vh', minHeight: '12rem' }}
            >
                {loading && items.length === 0 ? (
                    <div className="flex flex-column gap-2 p-3">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="flex align-items-center gap-3">
                                <Skeleton shape="circle" size="2.5rem" />
                                <div className="flex-1">
                                    <Skeleton width="60%" height="1rem" className="mb-1" />
                                    <Skeleton width="80%" height="0.75rem" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-column align-items-center text-center p-5 gap-2">
                        <div
                            className="flex align-items-center justify-content-center border-circle"
                            style={{ width: '3rem', height: '3rem', backgroundColor: 'var(--surface-100)' }}
                        >
                            <i
                                className="pi pi-users text-500"
                                style={{ fontSize: '1.1rem' }}
                                aria-hidden="true"
                            />
                        </div>
                        <p className="text-600 text-sm m-0">
                            {debouncedSearch
                                ? `No mutuals match "${debouncedSearch}"`
                                : 'No mutual connections yet'}
                        </p>
                    </div>
                ) : (
                    <ul className="list-none p-0 m-0">
                        {items.map((p) => (
                            <li key={p.id}>
                                <button
                                    type="button"
                                    onClick={() => handleRowClick(p.id)}
                                    className="flex align-items-center gap-3 w-full text-left p-3 hover:surface-100 transition-colors transition-duration-150 border-none surface-card cursor-pointer"
                                    style={{ borderBottom: '1px solid var(--surface-100)' }}
                                >
                                    <Avatar
                                        label={(p.full_name || 'U').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                                        image={p.profile_photo_url}
                                        shape="circle"
                                        style={{
                                            width: '2.5rem',
                                            height: '2.5rem',
                                            fontSize: '0.85rem',
                                            fontWeight: 700,
                                            backgroundColor: 'var(--blue-100)',
                                            color: 'var(--blue-700)',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                            {p.full_name}
                                        </div>
                                        {subtitleFor(p) && (
                                            <div className="text-600 text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                {subtitleFor(p)}
                                            </div>
                                        )}
                                    </div>
                                    <i
                                        className="pi pi-chevron-right text-400 flex-shrink-0"
                                        style={{ fontSize: '0.75rem' }}
                                        aria-hidden="true"
                                    />
                                </button>
                            </li>
                        ))}

                        {/* Sentinel + loader/end markers */}
                        <li ref={sentinelRef} className="flex justify-content-center p-3">
                            {loadingMore ? (
                                <ProgressSpinner
                                    style={{ width: '1.5rem', height: '1.5rem' }}
                                    strokeWidth="4"
                                />
                            ) : page >= lastPage && items.length > 0 ? (
                                <span className="text-500 text-xs">End of list</span>
                            ) : null}
                        </li>
                    </ul>
                )}
            </div>
        </Dialog>
    );
};

export default MutualConnectionsDialog;
